import React from 'react';
import {
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Box,
  Chip,
  Avatar,
  AvatarGroup,
  Grid,
  CardMedia
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import EuroIcon from '@mui/icons-material/Euro';
import LockIcon from '@mui/icons-material/Lock';
import EventIcon from '@mui/icons-material/Event';
import { getTransformedImageUrl } from '../../../utils/filestack';

const EventCard = ({ event, onClick }) => {
  const { t } = useTranslation('events');
  const isEventPast = new Date(event.date) < new Date();
  
  // Get attending participants
  const attendingParticipants = event.participants?.filter(p => p.status === 'attending') || [];
  
  return (
    <Card 
      elevation={2} 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
        },
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: 'background.paper',
      }}
    >
      <CardActionArea 
        onClick={() => onClick(event._id)} 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start',
          justifyContent: 'flex-start'
        }}
      >
        {/* Display event image if available */}
        {event.imageUrl ? (
          <CardMedia
            component="img"
            height="140"
            image={getTransformedImageUrl(event.imageUrl, 'gallery')}
            alt={event.title}
            sx={{ objectFit: 'cover' }}
          />
        ) : (
          <Box 
            sx={{ 
              height: 80, 
              width: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: 'action.hover'
            }}
          >
            <EventIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          </Box>
        )}
        
        <CardContent sx={{ 
          width: '100%', 
          flexGrow: 1, 
          p: { xs: 2.5, md: 3 },
          '&:last-child': { pb: { xs: 2.5, md: 3 } }
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography 
              variant="h6" 
              component="div" 
              noWrap 
              sx={{ 
                maxWidth: { xs: '85%', md: '75%' },
                fontSize: { xs: '1.1rem', md: '1.25rem' },
                fontWeight: 600
              }}
            >
              {event.title}
              {!event.isPublic && (
                <LockIcon sx={{ ml: 0.5, verticalAlign: 'middle', fontSize: '0.9rem', color: 'text.secondary' }} />
              )}
            </Typography>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Chip 
                label={isEventPast ? t('eventList.past') : t('eventList.upcoming')} 
                color={isEventPast ? 'default' : 'primary'} 
                size="small" 
                sx={{ 
                  borderRadius: '8px',
                  fontWeight: 500
                }}
              />
            </Box>
          </Box>
          
          <Typography 
            variant="body2" 
            sx={{ 
              mb: 2, 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: { xs: 'none', sm: '-webkit-box' },
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              height: { xs: 'auto', sm: '40px' },
              color: 'text.secondary',
              fontSize: '0.9rem'
            }}
          >
            {event.description}
          </Typography>
          
          <Grid container spacing={2} sx={{ mt: { xs: 0.5, md: 1 } }}>
            <Grid item xs={12} sm={12}>
              <Box display="flex" alignItems="center">
                <LocationOnIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {event.location}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={12}>
              <Box display="flex" alignItems="center">
                <CalendarTodayIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {format(new Date(event.date), 'PPP')}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6}>
              <Box display="flex" alignItems="center">
                <PeopleIcon sx={{ mr: 1, fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {attendingParticipants.length}
                  {event.maxParticipants && (
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, color: 'text.secondary' }}>
                      /{event.maxParticipants}
                    </Box>
                  )}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6}>
              {event.price > 0 && (
                <Box display="flex" alignItems="center" justifyContent="flex-end">
                  <EuroIcon sx={{ mr: 0.5, fontSize: '1rem', color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {event.price} 
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
          
          {attendingParticipants.length > 0 && (
            <Box 
              mt={2.5} 
              display="flex" 
              justifyContent="space-between" 
              alignItems="center"
              sx={{ display: { xs: 'none', sm: 'flex' } }}
            >
              <AvatarGroup 
                max={5} 
                sx={{ 
                  '& .MuiAvatar-root': { 
                    width: 28, 
                    height: 28, 
                    fontSize: '0.8rem',
                    border: '2px solid',
                    borderColor: 'background.paper'
                  } 
                }}
              >
                {attendingParticipants.map(participant => (
                  <Avatar 
                    key={participant.user._id} 
                    alt={participant.user.name}
                    title={participant.user.name}
                    sx={{ backgroundColor: 'primary.main' }}
                  >
                    {participant.user.name.charAt(0)}
                  </Avatar>
                ))}
              </AvatarGroup>
              
              {attendingParticipants.length > 5 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  +{attendingParticipants.length - 5} {t('eventDetail.more')}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default EventCard; 